"use client";

import * as React from "react";

import { Transition } from "@headlessui/react";

import { cn } from "@/shared/helpers/cn.helper";

import { useMapPanelContext } from "./map-panel-context";
import { MapPanelIndicator } from "./map-panel-indicator";

interface MapPanelTabsProps {
  className?: string;
  style?: React.CSSProperties;
}

export function MapPanelTabs({ className, style }: MapPanelTabsProps) {
  const { tabs, setActiveKey, setOpen, side, open } = useMapPanelContext();

  const sideClass = side === "left" ? "left-4" : "right-4";

  if (!tabs.length) return null;

  return (
    <Transition
      enter="transition-opacity duration-200 ease-out"
      enterFrom="opacity-0"
      enterTo="opacity-100"
      leave="transition-opacity duration-200 ease-in"
      leaveFrom="opacity-100"
      leaveTo="opacity-0"
      show={!open}
    >
      <div
        className={cn(
          "pointer-events-auto absolute top-4 z-30",
          sideClass,
          className,
        )}
        style={style}
      >
        <div className="border-light-100 flex flex-col gap-6 rounded-xl border bg-white p-2.5 shadow-lg">
          {tabs.map((tab) => {
            const IconComponent = React.isValidElement(tab.icon)
              ? (tab.icon.type as React.ElementType)
              : null;

            return (
              <button
                key={tab.key}
                className={cn(
                  "relative",
                  "flex items-center justify-center",
                  "h-10 max-h-10 min-h-10 w-10 min-w-10 max-w-10",
                  "text-light-400 rounded-lg bg-transparent",
                  "hover:text-light-600",
                  "active:bg-light-100 active:text-light-600",
                  "selection:bg-transparent",
                  "disabled:text-light-200 disabled:pointer-events-none disabled:bg-transparent",
                )}
                onClick={() => {
                  setActiveKey(tab.key);
                  setOpen(true);
                }}
              >
                {IconComponent && (
                  <IconComponent
                    className="pointer-events-none h-6 w-6 text-inherit"
                  />
                )}
                {tab.indicator && (
                  <MapPanelIndicator indicator={tab.indicator} />
                )}
              </button>
            );
          })}
        </div>
      </div>
    </Transition>
  );
}
